/**
 * Login page - Cognito authentication.
 * Handles both regular sign-in and first-time password change.
 */

import React, { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import * as authService from '../services/auth.service';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { LogIn, AlertCircle, ShieldCheck } from 'lucide-react';

export default function LoginPage() {
  const { login, completeNewPassword, logout, error, clearError, isLoading, needsNewPassword } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState('');

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    clearError();
    setLocalError('');
    const ok = await login(email, password);
    if (!ok) return;

    // Admin accounts cannot use the developer portal
    if (authService.isAdmin()) {
      setLocalError('Admin accounts cannot sign in to the developer portal.');
      logout();
      return;
    }

    if (!needsNewPassword) navigate('/dashboard');
  };

  const handleNewPassword = async (e: FormEvent) => {
    e.preventDefault();
    clearError();
    setLocalError('');

    if (newPassword !== confirmPassword) {
      setLocalError('Passwords do not match.');
      return;
    }

    if (newPassword.length < 12) {
      setLocalError('Password must be at least 12 characters.');
      return;
    }

    const ok = await completeNewPassword(newPassword);
    if (!ok) return;

    if (authService.isAdmin()) {
      setLocalError('Admin accounts cannot sign in to the developer portal.');
      logout();
      return;
    }

    navigate('/dashboard');
  };

  const displayError = error || localError;

  // New Password form (first login with temp password)
  if (needsNewPassword) {
    return (
      <div className="auth-page">
        <Navbar />
        <div className="auth-container">
          <div className="auth-card">
            <div className="auth-header">
              <ShieldCheck size={28} />
              <h1>Set New Password</h1>
              <p>Your admin created a temporary password. Please set a new one.</p>
            </div>

            {displayError && (
              <div className="alert alert-error">
                <AlertCircle size={16} /> {displayError}
              </div>
            )}

            <form onSubmit={handleNewPassword} className="auth-form">
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  id="newPassword"
                  type="password"
                  placeholder="Min 12 chars, upper, lower, number, symbol"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={12}
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  id="confirmPassword"
                  type="password"
                  placeholder="Re-enter your new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>

              <button type="submit" className="btn btn-accent btn-block" disabled={isLoading}>
                {isLoading ? 'Setting password...' : 'Set Password & Sign In'}
              </button>
            </form>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Regular login form
  return (
    <div className="auth-page">
      <Navbar />
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <LogIn size={28} />
            <h1>Sign In</h1>
            <p>Access your developer dashboard</p>
          </div>

          {displayError && (
            <div className="alert alert-error">
              <AlertCircle size={16} /> {displayError}
            </div>
          )}

          <form onSubmit={handleLogin} className="auth-form" autoComplete="off">
            <div className="form-group">
              <label htmlFor="email">Username / Email</label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="off"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="off"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <button type="submit" className="btn btn-accent btn-block" disabled={isLoading}>
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <p className="auth-footer-text" style={{ lineHeight: 1.6 }}>
            Don't have an account? <Link to="/register">Request beta access</Link>
          </p>
          <p className="auth-footer-text">
            <Link to="/">Back to home</Link>
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}